import { profileAtom } from "@/atoms";
import authSheetAtom from "@/atoms/auth-sheet";
import { requestBirthdayPromptAtom } from "@/atoms/birthday-prompt";
import { useAnimatedCurrency } from "@/hooks/use-animated-currency";
import { useCartBumpScale } from "@/hooks/use-cart-bump";
import { useFlushAndGo } from "@/hooks/use-flush-and-go";
import { useMinimumOrder } from "@/hooks/use-minimum-order";
import { CART_TARGET_ATTR } from "@/lib/fly-to-cart";
import { MotionButton, Price, Spinner } from "@/components/ui";
import { TRANSITION } from "@/lib/motion";
import { Cart } from "@/types";
import { useAtomValue, useSetAtom } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { FixedBottomBar } from "./FixedBottomBar";

/** Fixed footer CTA showing the payable total; the minimum-order gate reads the gross bag total. */
export default function FinishCartButton({ cart, totalPrice }: { cart: Cart; totalPrice: number }) {
  const { minimum, isBelowMinimum } = useMinimumOrder();
  const profile = useAtomValue(profileAtom);
  const { isBusy, run } = useFlushAndGo();
  const navigate = useNavigate();
  const setAuthSheet = useSetAtom(authSheetAtom);
  const requestBirthdayPrompt = useSetAtom(requestBirthdayPromptAtom);

  const buttonControls = useCartBumpScale();
  const total = useAnimatedCurrency(totalPrice);
  const minimumLabel = useAnimatedCurrency(minimum);
  const discounted = totalPrice < cart.totalPrice;

  const handleClick = () =>
    run(() => {
      if (profile) {
        void requestBirthdayPrompt("order");
        navigate("/cart/checkout");
      } else {
        setAuthSheet({ open: true, redirectTo: "/cart/checkout" });
      }
    });

  return (
    <FixedBottomBar>
      {!isBelowMinimum(cart.totalPrice) ? (
        <MotionButton
          onClick={handleClick}
          disabled={isBusy}
          animate={buttonControls}
          data-testid="finish-cart-button"
          size="lg"
          align="between"
          fullWidth
        >
          {isBusy ? (
            <>
              <span className="flex items-center gap-2">
                <Spinner />
                Salvando sacola...
              </span>
              <motion.span>{total}</motion.span>
            </>
          ) : (
            <>
              <span>Finalizar pedido</span>
              <span className="flex items-baseline gap-2">
                <AnimatePresence initial={false}>
                  {discounted ? (
                    <motion.span
                      key="gross"
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0, transition: TRANSITION.fade }}
                      exit={{ opacity: 0, x: 8, transition: TRANSITION.exit }}
                    >
                      <span className="sr-only">De </span>
                      <Price value={cart.totalPrice} strike size="sm" weight="normal" tone="on-primary" />
                      <span className="sr-only"> por</span>
                    </motion.span>
                  ) : null}
                </AnimatePresence>
                <motion.span className="tabular-nums" {...{ [CART_TARGET_ATTR]: "" }}>
                  {total}
                </motion.span>
              </span>
            </>
          )}
        </MotionButton>
      ) : (
        <MotionButton
          disabled
          animate={buttonControls}
          data-testid="finish-cart-button"
          size="lg"
          align="between"
          fullWidth
        >
          <span>Preço mínimo</span>
          <motion.span {...{ [CART_TARGET_ATTR]: "" }}>{minimumLabel}</motion.span>
        </MotionButton>
      )}
    </FixedBottomBar>
  );
}
